<template>
  <div class="nevs-content">
    <ModalCheckMember :show="checkMemberModal" :preselect-person-id="checkMemberPreselectId" @done="checkMemberDone"></ModalCheckMember>
    <ModalQuickInvoice :show="quickInvoiceModal" @done="quickInvoiceModal = false"></ModalQuickInvoice>
    <ModalVisit :show="editVisitModal.show" :visit="editVisitModal.visit" @done="editVisitDone"></ModalVisit>
    <div class="dashboard-actions">
      <button class="action-tile" type="button" @click="openCheckMember">
        <span class="action-tile-icon">
          <!-- member walking in through a doorway -->
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4"/>
            <path d="M3 12h11"/>
            <path d="M10 8l4 4-4 4"/>
          </svg>
        </span>
        <span class="action-tile-label">{{ $LANG.Get('buttons.checkInMember') }}</span>
      </button>
      <button class="action-tile" type="button" @click="addPerson">
        <span class="action-tile-icon">
          <!-- person silhouette with a plus -->
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="9" cy="8" r="3.2"/>
            <path d="M3.5 20a6 6 0 0 1 11 0"/>
            <path d="M18 8v6"/>
            <path d="M15 11h6"/>
          </svg>
        </span>
        <span class="action-tile-label">{{ $LANG.Get('buttons.addPerson') }}</span>
      </button>
      <button class="action-tile" type="button" @click="quickInvoiceModal = true">
        <span class="action-tile-icon">
          <!-- invoice document with itemised lines -->
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M8 3h5l5 5v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/>
            <path d="M13 3v5h5"/>
            <path d="M9.5 12.5h5"/>
            <path d="M9.5 16h3"/>
          </svg>
        </span>
        <span class="action-tile-label">{{ $LANG.Get('buttons.quickInvoice') }}</span>
      </button>
    </div>
    <NevsCard>
      <h2 class="visits-grid-title">{{ $LANG.Get('labels.currentlyInCenter') }} ({{ activeVisits.length }})</h2>
      <div class="visits-grid-scroll">
      <NevsSimpleGrid :fields="visitFields" :records="activeVisits" :empty-text="$LANG.Get('labels.noActiveVisits')"
                      fixed-layout @row-click="(record) => openPerson(record.person_id)">
        <template #cell-membership="{ record }">
          <span v-if="record.is_paused" class="membership-pill membership-pill-paused">{{ $LANG.Get('labels.paused') }}</span>
          <template v-if="record.membership_names.length > 0">
            <span v-for="(name, key) in record.membership_names" :key="key"
                  class="membership-pill membership-pill-active">{{ name }}</span>
          </template>
          <span v-else class="membership-pill membership-pill-none">{{ $LANG.Get('labels.noMembership') }}</span>
        </template>
        <template #cell-products="{ record }">
          <span v-if="record.products.length === 0">-</span>
          <div v-for="(product, key) in record.products" :key="key" class="visit-product">
            {{ product.product_name }} <span class="visit-product-quantity">({{ product.quantity_display }})</span>
          </div>
        </template>
        <template #actions="{ record }">
          <span v-if="record.note" class="visit-note-icon" :title="record.note" @click="showNote(record.note)"><i class="fa-solid fa-note-sticky"></i></span>
          <span v-if="record.products.length > 0" :title="$LANG.Get('tooltips.edit')" class="visits-grid-button visits-grid-button-edit"
                @click="editVisit(record)"><i class="fa-solid fa-pencil"></i></span>
          <span :title="$LANG.Get('tooltips.checkOut')" class="visits-grid-button visits-grid-button-checkout"
                @click="checkoutVisit(record)"><i class="fa-solid fa-right-from-bracket"></i></span>
          <span :title="$LANG.Get('tooltips.openInNewWindow')" class="visits-grid-button"
                @click="openPerson(record.person_id, true)"><i class="fa-solid fa-up-right-from-square"></i></span>
        </template>
      </NevsSimpleGrid>
      </div>
    </NevsCard>
    <NevsCard class="departed-card">
      <h2 class="visits-grid-title">{{ $LANG.Get('labels.departedToday') }} ({{ departedVisits.length }})</h2>
      <div class="visits-grid-scroll">
      <NevsSimpleGrid :fields="departedFields" :records="departedVisits" :empty-text="$LANG.Get('labels.noDepartedToday')"
                      fixed-layout @row-click="(record) => openPerson(record.person_id)">
        <template #cell-time="{ record }">
          {{ record.checked_in_time_display }}<template v-if="record.checked_out_time_display"> &ndash; {{ record.checked_out_time_display }}</template>
        </template>
        <template #cell-membership="{ record }">
          <span v-if="record.is_paused" class="membership-pill membership-pill-paused">{{ $LANG.Get('labels.paused') }}</span>
          <template v-if="record.membership_names.length > 0">
            <span v-for="(name, key) in record.membership_names" :key="key"
                  class="membership-pill membership-pill-active">{{ name }}</span>
          </template>
          <span v-else class="membership-pill membership-pill-none">{{ $LANG.Get('labels.noMembership') }}</span>
        </template>
        <template #cell-products="{ record }">
          <span v-if="record.products.length === 0">-</span>
          <div v-for="(product, key) in record.products" :key="key" class="visit-product">
            {{ product.product_name }} <span class="visit-product-quantity">({{ product.quantity_display }})</span>
          </div>
        </template>
        <template #actions="{ record }">
          <span v-if="record.note" class="visit-note-icon" :title="record.note" @click="showNote(record.note)"><i class="fa-solid fa-note-sticky"></i></span>
          <span v-if="record.products.length > 0" :title="$LANG.Get('tooltips.edit')" class="visits-grid-button visits-grid-button-edit"
                @click="editVisit(record)"><i class="fa-solid fa-pencil"></i></span>
          <span :title="$LANG.Get('tooltips.openInNewWindow')" class="visits-grid-button"
                @click="openPerson(record.person_id, true)"><i class="fa-solid fa-up-right-from-square"></i></span>
        </template>
      </NevsSimpleGrid>
      </div>
    </NevsCard>
  </div>
</template>

<script>
import {Centrifuge} from "centrifuge";
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsSimpleGrid from "@/components/nevs/NevsSimpleGrid.vue";
import ModalCheckMember from "@/components/modals/ModalCheckMember.vue";
import ModalQuickInvoice from "@/components/modals/ModalQuickInvoice.vue";
import ModalVisit from "@/components/modals/ModalVisit.vue";

export default {
  name: "ModuleDashboard",
  components: {ModalVisit, ModalQuickInvoice, ModalCheckMember, NevsSimpleGrid, NevsCard},
  data() {
    return {
      checkMemberModal: false,
      checkMemberPreselectId: null,
      centrifuge: null,
      visitsTimer: null,
      quickInvoiceModal: false,
      editVisitModal: {
        show: false,
        visit: null
      },
      activeVisits: [],
      departedVisits: [],
      // Widths are shared with departedFields below so both dashboard grids line up column-for-column.
      visitFields: [
        {name: 'checked_in_at_display', label: this.$LANG.Get('fields.checkedInAt'), width: '16%'},
        {name: 'first_name', label: this.$LANG.Get('fields.firstName'), width: '16%'},
        {name: 'last_name', label: this.$LANG.Get('fields.lastName'), width: '16%'},
        {name: 'membership', label: this.$LANG.Get('fields.membership'), width: '24%'},
        {name: 'products', label: this.$LANG.Get('fields.products'), width: '18%'}
      ],
      departedFields: [
        {name: 'time', label: this.$LANG.Get('fields.time'), width: '16%'},
        {name: 'first_name', label: this.$LANG.Get('fields.firstName'), width: '16%'},
        {name: 'last_name', label: this.$LANG.Get('fields.lastName'), width: '16%'},
        {name: 'membership', label: this.$LANG.Get('fields.membership'), width: '24%'},
        {name: 'products', label: this.$LANG.Get('fields.products'), width: '18%'}
      ]
    }
  },
  watch: {
    // A check-in done from the top bar button (App.vue) instead of this screen: reload both grids.
    '$store.state.visitsVersion'() {
      this.loadVisits();
    }
  },
  methods: {
    showNote(note) {
      this.$LOCAL_BUS.TriggerEvent('popup', {
        type: 'alert',
        text: note.split('\n').join('<br>')
      });
    },
    // Loads both dashboard grids: who is in the Sports Center now and who has already left today.
    loadVisits(silent = false) {
      this.loadActiveVisits(silent);
      this.loadDepartedVisits(silent);
    },
    loadActiveVisits(silent = false) {
      let vm = this;
      // The 30-second auto-refresh runs silently: no global loader flash and no error popups if a poll fails.
      this.$API.APICall('get', 'visits/active', {}, (data, success) => {
        if (success) {
          vm.activeVisits = data.records;
        } else if (!silent) {
          vm.$HELPERS.HandleServerError(vm, data);
        }
      }, !silent);
    },
    loadDepartedVisits(silent = false) {
      let vm = this;
      this.$API.APICall('get', 'visits/departed-today', {}, (data, success) => {
        if (success) {
          vm.departedVisits = data.records;
        } else if (!silent) {
          vm.$HELPERS.HandleServerError(vm, data);
        }
      }, !silent);
    },
    editVisit(record) {
      this.editVisitModal.visit = record;
      this.editVisitModal.show = true;
    },
    editVisitDone(success) {
      this.editVisitModal.show = false;
      this.editVisitModal.visit = null;
      if (success) {
        this.loadVisits();
      }
    },
    checkoutVisit(record) {
      let vm = this;
      this.$LOCAL_BUS.TriggerEvent('popup', {
        type: 'confirm', text: this.$LANG.Get('alerts.confirmVisitCheckout'), callback: (response) => {
          if (!response) return;
          vm.$API.APICall('post', 'visits/checkout/' + record.id, {}, (data, success) => {
            if (success) {
              vm.loadVisits();
            } else {
              vm.$HELPERS.HandleServerError(vm, data);
            }
          });
        }
      });
    },
    checkMemberDone(success) {
      this.checkMemberModal = false;
      this.checkMemberPreselectId = null;
      if (success) {
        this.loadVisits();
      }
    },
    openCheckMember() {
      // Manual check-in from the button: no preselected person.
      this.checkMemberPreselectId = null;
      this.checkMemberModal = true;
    },
    connectRealtime() {
      let vm = this;
      // Fetch the WebSocket URL and an initial connection token (which auto-subscribes this session to its
      // client's fast-events channel), then open the Centrifugo connection. Events raised elsewhere - e.g. a
      // member tapping "Ulaz" - arrive as pushed publications instead of being polled for.
      this.$API.APICall('get', 'realtime-token', {}, (data, success) => {
        if (!success || !data.ws_url || !data.token) return;
        const centrifuge = new Centrifuge(data.ws_url, {
          token: data.token,
          getToken: () => vm.fetchRealtimeToken()
        });
        // With Centrifugo server-side subscriptions the events for our channel surface as client-level
        // publications.
        centrifuge.on('publication', (ctx) => vm.handleRealtimeEvent(ctx.data));
        centrifuge.connect();
        vm.centrifuge = centrifuge;
      }, false);
    },
    disconnectRealtime() {
      if (this.centrifuge !== null) {
        this.centrifuge.disconnect();
        this.centrifuge = null;
      }
    },
    fetchRealtimeToken() {
      // Resolves a fresh connection token; Centrifugo calls this to renew the token before it expires.
      let vm = this;
      return new Promise((resolve, reject) => {
        vm.$API.APICall('get', 'realtime-token', {}, (data, success) => {
          if (success && data.token) {
            resolve(data.token);
          } else {
            reject(new Error('realtime token fetch failed'));
          }
        }, false);
      });
    },
    handleRealtimeEvent(data) {
      if (!data || data.event_type !== 'PERSON_ENTRY' || !data.details) return;
      if (data.details.automatic) {
        // The visit was already toggled server-side: just refresh the grid and announce who came/went.
        this.announceAutomaticEntry(data.details);
      } else if (data.details.person_id) {
        this.handlePersonEntry(data.details.person_id);
      }
    },
    announceAutomaticEntry(details) {
      this.loadVisits(true);
      let name = details.person_name || '';
      let key = details.action === 'exit' ? 'alerts.autoMemberExit' : 'alerts.autoMemberEntry';
      this.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get(key).replace('%name%', name)});
    },
    handlePersonEntry(personId) {
      // Open the check-in modal on the entering person - or, if it is already open, just switch to them.
      this.checkMemberPreselectId = personId;
      if (!this.checkMemberModal) {
        this.checkMemberModal = true;
      }
    },
    openPerson(id, newWindow = false) {
      if (newWindow) {
        let routeData = this.$router.resolve('/persons/' + id);
        window.open(routeData.href);
      } else {
        this.$router.push('/persons/' + id);
      }
    },
    addPerson() {
      // id 0 opens EntityPerson in "new person" (add) mode; open it in a new tab.
      let routeData = this.$router.resolve('/persons/0');
      window.open(routeData.href);
    }
  },
  mounted() {
    this.$store.commit('selectMenu', 'dashboard');
    this.$store.commit('selectSubMenu', null);
    this.$store.commit('setBreadcrumbs', [
      {
        label: this.$LANG.Get('modules.dashboard'),
        link: null
      }
    ]);
    this.loadVisits();
    // Only while the dashboard module is open: subscribe to fast events over Centrifugo (the connection is
    // closed in unmounted, so it stops the moment the user navigates away).
    this.connectRealtime();
    // Keep both visit grids fresh while the dashboard is open (silent background refresh).
    this.visitsTimer = setInterval(() => this.loadVisits(true), 30000);
  },
  unmounted() {
    this.disconnectRealtime();
    if (this.visitsTimer !== null) {
      clearInterval(this.visitsTimer);
      this.visitsTimer = null;
    }
  }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;
@use '../../scss/palette' as p;

.dashboard-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.action-tile {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  border: 1px solid p.$sand-300;
  border-radius: 16px;
  background: p.$white;
  box-shadow: 0 2px 6px rgba(94, 62, 30, 0.10);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.action-tile:hover {
  transform: translateY(-3px);
  border-color: p.$orange;
  box-shadow: 0 12px 24px rgba(94, 62, 30, 0.18);
}

.action-tile:active {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(94, 62, 30, 0.14);
}

.action-tile:focus-visible {
  outline: 2px solid p.$orange;
  outline-offset: 2px;
}

.action-tile-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 14px;
  color: p.$white;
  background: linear-gradient(135deg, p.$orange-bright, p.$orange);
  box-shadow: 0 5px 12px rgba(251, 93, 24, 0.35);
  transition: box-shadow 0.15s ease;
}

.action-tile:hover .action-tile-icon {
  box-shadow: 0 8px 18px rgba(251, 93, 24, 0.45);
}

.action-tile-icon svg {
  width: 27px;
  height: 27px;
}

.action-tile-label {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.25;
  color: p.$ink-strong;
}

@media (max-width: 720px) {
  .dashboard-actions {
    grid-template-columns: 1fr;
  }
}

.visits-grid-title {
  margin: 0 0 14px;
  font-size: 18px;
  font-weight: 600;
  color: p.$ink-strong;
}

.departed-card {
  margin-top: 24px;
}

// Keeps the visits table inside its card on narrow screens: the table scrolls horizontally instead of
// stretching the white card past the viewport.
.visits-grid-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  :deep(.nevs-simple-grid) {
    // Below this the fixed-percentage columns + actions column get unreadable, so we scroll instead.
    min-width: 640px;
  }
}

.visits-grid-button {
  cursor: pointer;
}

.visits-grid-button-edit {
  margin-right: 14px;
}

.visits-grid-button-checkout {
  margin-right: 14px;
  color: $danger;
}

.membership-pill {
  display: inline-block;
  text-align: center;
  padding: 5px 10px;
  border-radius: 5px;
  margin: 2px;
}

.membership-pill-active {
  background: $success-badge;
}

.membership-pill-none {
  background: $danger-bright;
  color: $white;
}

/* Shown alongside the memberships, not instead of them: the person holds them, they are just frozen. */
.membership-pill-paused {
  background: $warning;
  color: $white;
}

.visit-product-quantity {
  color: $ink-muted;
}

.visit-note-icon {
  color: $warning;
  margin-right: 12px;
  cursor: pointer;
}
</style>
